import Image from 'next/image';
import type { GalleryItem, Palette } from './types';

type ProofProps = { palette: Palette; galleryItems: GalleryItem[] };

export function ProofSection({ palette, galleryItems }: ProofProps) {
  return (
    <section 
      id="proof" 
      className="relative overflow-hidden px-6 py-16 scroll-mt-24 md:scroll-mt-28 lg:px-12"
      style={{ backgroundColor: '#F9F9F7' }} // Creamy background to contrast with the dark Pricing section
    >
      {/* --- BACKGROUND ATMOSPHERE --- */}
      {/* Subtle depth blobs */}
      <div 
        className="absolute left-0 top-0 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-30 blur-[100px]"
        style={{ background: palette.sage }}
      />
      <div 
        className="absolute right-0 bottom-0 h-[400px] w-[400px] translate-x-1/3 rounded-full opacity-20 blur-[80px]"
        style={{ background: palette.forest }}
      />

      <div className="relative mx-auto max-w-7xl">
        
        {/* HEADER: Tight & Aligned */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between mb-12">
          
          {/* Left: Title & Context */}
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-500 backdrop-blur-sm">
               <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
               Real Results
            </div>
            
            <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
              See what <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-800">peace of mind</span> looks like.
            </h2>
            
            <p className="mt-3 text-base leading-relaxed text-slate-600">
              These homeowners were once exactly where you are now. 
              Now, they've crossed the finish line and are simply enjoying their homes.
            </p>
          </div>
          
          {/* Right: Primary Action */}
          <div className="shrink-0 lg:text-right">
            <a
              href="#quote"
              className="group relative inline-flex items-center gap-2 rounded-full px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white shadow-xl transition-all hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-amber-500/20"
              style={{ 
                background: `linear-gradient(to right, ${palette.clay}, #b45309)` 
              }}
            >
              Make my home next
              <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              
              {/* Shine Overlay */}
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:animate-shimmer" />
            </a>
          </div>
        </div>

        {/* GALLERY GRID */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {galleryItems.map((item, idx) => (
            <div 
              key={item.src} 
              className="group relative h-80 overflow-hidden rounded-2xl bg-white p-2 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-emerald-900/10"
            >
              {/* The Image Container (Acts like a matte/frame) */}
              <div className="relative h-full w-full overflow-hidden rounded-xl">
                <Image 
                  src={item.src} 
                  alt={item.alt} 
                  fill 
                  className="object-cover transition-transform duration-700 group-hover:scale-110" 
                />
                
                {/* Gradient Overlay (Only appears on hover for cleaner initial look) */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                
                {/* Text Label (Slides up on hover) */}
                <div className="absolute bottom-0 left-0 w-full p-6 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="text-sm font-bold text-white">
                    {item.label}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                     <span className="h-0.5 w-4 bg-emerald-400" />
                     <p className="text-[10px] uppercase tracking-wider text-emerald-200">
                       Project Complete
                     </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Subtle bottom note */}
        <div className="mt-8 text-center">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">
                Trusted by 500+ Texas Families
            </p>
        </div>
        
      </div>
    </section>
  );
}
